import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserWizardData, User } from '../../../types/user.types';
import {
  validateUserEmail,
  validateUserName,
  validateUserPhone,
  validateUserRoles,
  validateUserPrimaryLocation,
} from '../../../utils/validators/user';

export interface UseUserWizardProps {
  initialUser?: User | null;
  onSave: (data: UserWizardData) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
}

export interface UseUserWizardReturn {
  currentStep: number;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  formData: UserWizardData;
  setFormData: React.Dispatch<React.SetStateAction<UserWizardData>>;
  updateField: (
    name: keyof UserWizardData,
    value: string | string[] | boolean | number | null,
  ) => void;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  validateStep: (step: number) => boolean;
  handleSave: () => Promise<void>;
  handleReset: () => void;
  handleCloseClick: () => void;
  cancelOperation: () => void;
  showCancelConfirmation: boolean;
  setShowCancelConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  hasChanges: boolean;
  isEditMode: boolean;
}

const INITIAL_FORM_DATA: UserWizardData = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  roleIds: [],
  isActive: true,
  primaryLocationId: null,
  canSellFromOtherLocations: false,
};

export const useUserWizard = ({
  initialUser,
  onSave,
  onClose,
  isOpen,
}: UseUserWizardProps): UseUserWizardReturn => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const initialFormDataRef = useRef<UserWizardData | null>(null);
  const isEditModeRef = useRef<boolean>(false);
  const lastFormDataRef = useRef<string>('');

  const [formData, setFormData] = useState<UserWizardData>(() => {
    const data = { ...INITIAL_FORM_DATA };
    initialFormDataRef.current = { ...data };
    lastFormDataRef.current = JSON.stringify(data);
    return data;
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isEditMode = isEditModeRef.current;

  useEffect(() => {
    if (!isOpen) {
      const resetData = { ...INITIAL_FORM_DATA };
      setFormData(resetData);
      setCurrentStep(1);
      setErrors({});
      setShowCancelConfirmation(false);
      initialFormDataRef.current = { ...resetData };
      lastFormDataRef.current = JSON.stringify(resetData);
      isEditModeRef.current = false;
      return;
    }

    if (initialUser) {
      const data: UserWizardData = {
        email: initialUser.email || '',
        firstName: initialUser.firstName || '',
        lastName: initialUser.lastName || '',
        phone: initialUser.phone || '',
        roleIds: initialUser.roles?.map((role) => role.id) || [],
        isActive: initialUser.isActive ?? true,
        primaryLocationId: initialUser.primaryLocationId ?? null,
        canSellFromOtherLocations: initialUser.canSellFromOtherLocations ?? false,
      };
      setFormData(data);
      setCurrentStep(1);
      setErrors({});
      initialFormDataRef.current = { ...data };
      lastFormDataRef.current = JSON.stringify(data);
      isEditModeRef.current = true;
    } else {
      const data = { ...INITIAL_FORM_DATA };
      setFormData(data);
      setCurrentStep(1);
      setErrors({});
      initialFormDataRef.current = { ...data };
      lastFormDataRef.current = JSON.stringify(data);
      isEditModeRef.current = false;
    }
  }, [isOpen, initialUser]);

  useEffect(() => {
    if (isOpen && initialFormDataRef.current) {
      lastFormDataRef.current = JSON.stringify(formData);
    }
  }, [formData, isOpen]);

  const updateField = useCallback(
    (name: keyof UserWizardData, value: string | string[] | boolean | number | null) => {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      });
    },
    [],
  );

  const validateStep = useCallback(
    (step: number) => {
      const nextErrors: Record<string, string> = {};

      if (step === 1) {
        const emailError = validateUserEmail(formData.email);
        if (emailError) nextErrors.email = emailError;

        const firstNameError = validateUserName(formData.firstName, 'firstName');
        if (firstNameError) nextErrors.firstName = firstNameError;

        const lastNameError = validateUserName(formData.lastName, 'lastName');
        if (lastNameError) nextErrors.lastName = lastNameError;

        const phoneError = validateUserPhone(formData.phone || '');
        if (phoneError) nextErrors.phone = phoneError;

        const locationError = validateUserPrimaryLocation(formData.primaryLocationId);
        if (locationError) nextErrors.primaryLocationId = locationError;
      }

      if (step === 2) {
        const rolesError = validateUserRoles(formData.roleIds);
        if (rolesError) nextErrors.roleIds = rolesError;
      }

      setErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    },
    [formData],
  );

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 3) {
      setCurrentStep(step);
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep >= 3) return;
    const isValid = validateStep(currentStep);
    if (isValid) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, validateStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const canGoNext = useMemo(() => {
    if (currentStep === 1) {
      return (
        formData.email.trim().length > 0 &&
        formData.firstName.trim().length > 0 &&
        formData.lastName.trim().length > 0 &&
        !!formData.primaryLocationId
      );
    }
    if (currentStep === 2) {
      return formData.roleIds.length > 0;
    }
    return false;
  }, [currentStep, formData]);

  const canGoPrev = useMemo(() => currentStep > 1, [currentStep]);

  const hasChanges = useMemo(() => {
    if (!initialFormDataRef.current) return false;
    return JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);
  }, [formData]);

  const handleSave = useCallback(async () => {
    const isValidStep1 = validateStep(1);
    const isValidStep2 = validateStep(2);
    if (!isValidStep1 || !isValidStep2) {
      if (!isValidStep1) {
        setCurrentStep(1);
      } else if (!isValidStep2) {
        setCurrentStep(2);
      }
      return;
    }

    try {
      await onSave(formData);
    } catch (error: any) {
      // Si el error tiene errores de validación mapeados, aplicarlos al formulario
      if (error?.validationErrors) {
        const validationErrors = error.validationErrors as Record<string, string>;
        setErrors(validationErrors);

        // Ir al paso que tiene el primer error
        if (
          validationErrors.email ||
          validationErrors.firstName ||
          validationErrors.lastName ||
          validationErrors.phone ||
          validationErrors.primaryLocationId
        ) {
          setCurrentStep(1);
        } else if (validationErrors.roleIds) {
          setCurrentStep(2);
        }
        return;
      }
    }
  }, [formData, onSave, validateStep]);

  const handleReset = useCallback(() => {
    setFormData({ ...INITIAL_FORM_DATA });
    setCurrentStep(1);
    setErrors({});
  }, []);

  const handleCloseClick = useCallback(() => {
    if (hasChanges) {
      setShowCancelConfirmation(true);
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  const cancelOperation = useCallback(() => {
    setShowCancelConfirmation(false);
    setFormData({ ...INITIAL_FORM_DATA });
    setCurrentStep(1);
    setErrors({});
    initialFormDataRef.current = { ...INITIAL_FORM_DATA };
    isEditModeRef.current = false;
    onClose();
  }, [onClose]);

  return {
    currentStep,
    goToStep,
    nextStep,
    prevStep,
    canGoNext,
    canGoPrev,
    formData,
    setFormData,
    updateField,
    errors,
    setErrors,
    validateStep,
    handleSave,
    handleReset,
    handleCloseClick,
    cancelOperation,
    showCancelConfirmation,
    setShowCancelConfirmation,
    hasChanges,
    isEditMode: isEditModeRef.current,
  };
};
